// An on-screen rendering of the invoice PDF. It follows the same template
// settings as the QuestPDF layouts (classic, modern, compact) so the designer
// preview and the invoice page show what the customer will get. The server's
// PDF remains the document of record.
import type { CSSProperties } from 'react';
import { addMoney, formatMoney, isZero, mulMoney } from '@elysian/ui';
import type { LedgerDocument, OrganisationContext, TaxCode, TemplateLayout, TemplateSettings } from '../../data/source';
import { bankLine, type PrintedBank } from './printing';
import { longDate, merge } from './shared';

export interface PaperContact {
  name: string;
  email?: string | null;
  vat_number?: string | null;
  address?: { line1?: string; line2?: string; town?: string; county?: string; postcode?: string } | null;
}

const FONTS: Record<string, string> = {
  dm_sans: "'DM Sans', system-ui, sans-serif",
  source_serif: "'Source Serif 4', Georgia, serif",
  ibm_plex: "'IBM Plex Sans', system-ui, sans-serif",
  lora: "Lora, Georgia, serif",
};

export function InvoicePaper({ doc, contact, org, layout, settings, codes, bank = null, logoUrl = null }: {
  doc: Pick<LedgerDocument, 'doc_no' | 'status' | 'type' | 'issue_date' | 'due_date' | 'reference' | 'notes' | 'lines' | 'net_total' | 'tax_total' | 'gross_total' | 'outstanding' | 'currency'> & { fx_rate?: string };
  contact: PaperContact | undefined;
  org: OrganisationContext['organisation'] | undefined;
  layout: TemplateLayout;
  settings: TemplateSettings;
  codes: TaxCode[];
  /** The details printed under "Pay by bank transfer"; nothing is printed without them. */
  bank?: PrintedBank | null;
  logoUrl?: string | null;
}) {
  const f = settings.fields ?? {};
  const on = (k: string, dflt = true) => f[k] ?? dflt;
  const accent = settings.brand?.accent ?? '#0F6E56';
  const money = (v: string) => formatMoney(v, doc.currency);
  const credit = doc.type === 'sales_credit';
  const title = credit ? 'Credit note' : settings.title?.trim() || 'Invoice';
  const number = doc.doc_no ?? 'DRAFT';
  const code = (c?: string) => codes.find((t) => t.code === c);

  const vatRows = new Map<string, { net: string; tax: string }>();
  for (const l of doc.lines) {
    const k = l.tax_code ?? '';
    const r = vatRows.get(k) ?? { net: '0', tax: '0' };
    vatRows.set(k, { net: addMoney(r.net, l.net_amount), tax: addMoney(r.tax, l.tax_amount ?? '0') });
  }
  const reverseCharge = doc.lines.some((l) => code(l.tax_code)?.is_reverse_charge);
  const euGoods = doc.lines.some((l) => l.tax_code === 'EUGS');
  // Irish and UK VAT rules: an invoice in another currency also states the VAT in the home currency.
  const base = org?.base_currency ?? 'EUR';
  // Six places is what the money helpers take; stored rates can have eight.
  const rate = doc.currency !== base && doc.fx_rate && Number(doc.fx_rate) !== 1 ? Number(doc.fx_rate).toFixed(6) : null;

  const values: Record<string, string> = {
    'customer.name': contact?.name ?? '',
    'customer.email': contact?.email ?? '',
    'org.name': org?.legal_name ?? '',
    'org.vat_number': org?.vat_number ?? '',
    'invoice.number': number,
    'invoice.date': longDate(doc.issue_date),
    'invoice.due_date': longDate(doc.due_date),
    'invoice.total': money(doc.gross_total),
    'invoice.reference': doc.reference ?? '',
    'invoice.balance': money(doc.outstanding),
    year: doc.issue_date.slice(0, 4),
  };
  const blocks = (zone: string) =>
    (settings.blocks ?? []).filter((b) => b.zone === zone && (b.type === 'text' || (b.type === 'terms' && !credit)) && b.text)
      .map((b, i) => <p key={`${zone}${i}`} className="el-paper-block">{merge(b.text!, values)}</p>);

  const addr = contact?.address;
  const addressLines = addr ? [addr.line1, addr.line2, addr.town, addr.county, addr.postcode].filter(Boolean) : [];
  const style = { '--paper-accent': accent, fontFamily: FONTS[settings.brand?.font ?? 'dm_sans'] } as CSSProperties;
  const logoPos = settings.brand?.logo_position ?? 'left';
  // Millimetres on paper; about 3.8 screen pixels to the millimetre at this preview's scale.
  const logoHeight = Math.round((settings.brand?.logo_height_mm ?? 18) * 3.2);

  return (
    <article className="el-paper" data-layout={layout} style={style} aria-label={`${title} ${number}`}>
      {doc.status === 'draft' && <div className="el-paper-stamp" aria-hidden="true">Draft</div>}
      {doc.status === 'void' && <div className="el-paper-stamp" aria-hidden="true">Void</div>}
      <header className="el-paper-head" data-logo={logoPos}>
        <div className="el-paper-org">
          {logoUrl
            ? <img className="el-paper-logo-img" src={logoUrl} alt={`${org?.legal_name ?? 'Company'} logo`} style={{ height: logoHeight }} />
            : <div className="el-paper-logo" aria-hidden="true">{(org?.legal_name ?? 'E').slice(0, 1)}</div>}
          <div>
            <div className="el-paper-orgname">{org?.legal_name}</div>
            {org?.vat_number && <div className="el-paper-small">VAT {org.vat_number}</div>}
          </div>
        </div>
        <h2 className="el-paper-title">{title}</h2>
      </header>
      {blocks('header')}

      <div className="el-paper-meta">
        <div>
          <div className="el-paper-label">{credit ? 'Credit to' : 'Bill to'}</div>
          <strong>{contact?.name ?? 'Choose a customer'}</strong>
          {addressLines.map((l) => <div key={l}>{l}</div>)}
          {on('contact_email') && contact?.email && <div className="el-paper-small">{contact.email}</div>}
          {on('customer_vat_number') && contact?.vat_number && <div className="el-paper-small">VAT {contact.vat_number}</div>}
        </div>
        <dl className="el-paper-facts">
          <div><dt>{credit ? 'Credit note no.' : 'Invoice no.'}</dt><dd>{number}</dd></div>
          <div><dt>Date</dt><dd>{longDate(doc.issue_date)}</dd></div>
          {on('due_date') && !credit && doc.due_date && <div><dt>Due</dt><dd>{longDate(doc.due_date)}</dd></div>}
          {on('customer_reference') && doc.reference && <div><dt>Your reference</dt><dd>{doc.reference}</dd></div>}
        </dl>
      </div>
      {blocks('before_lines')}

      <div className="el-table-wrap">
        <table className="el-paper-lines">
          <thead>
            <tr>
              <th>Description</th><th className="el-num">Qty</th><th className="el-num">Unit price</th><th className="el-num">VAT</th>
              {on('line_vat_amount', false) && <th className="el-num">VAT amount</th>}
              <th className="el-num">Net</th>
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.description || <span className="el-muted">—</span>}</td>
                <td className="el-num">{l.quantity ?? '1'}</td>
                <td className="el-num">{money(l.unit_price || '0')}</td>
                <td className="el-num">{rateLabel(code(l.tax_code))}</td>
                {on('line_vat_amount', false) && <td className="el-num">{money(l.tax_amount ?? '0')}</td>}
                <td className="el-num">{money(l.net_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="el-paper-totals">
        <div><span>Net</span><span className="el-num">{money(doc.net_total)}</span></div>
        {[...vatRows].map(([c, r]) => (
          <div key={c} className="el-paper-small"><span>VAT {rateLabel(code(c))} on {money(r.net)}</span><span className="el-num">{money(r.tax)}</span></div>
        ))}
        <div className="el-paper-grand"><span>{credit ? 'Total credit' : 'Total due'}</span><span className="el-num">{money(doc.gross_total)}</span></div>
      </div>

      {rate && (
        <p className="el-paper-small el-paper-fx">
          In {base === 'EUR' ? 'euro' : base === 'GBP' ? 'sterling' : base}{' '}at {shownRate(rate)} per {doc.currency}:{' '}
          total {formatMoney(mulMoney(doc.gross_total, rate), base)}
          {!isZero(doc.tax_total) && <>, VAT {formatMoney(mulMoney(doc.tax_total, rate), base)}</>}.
        </p>
      )}
      {reverseCharge && <p className="el-paper-notice">VAT on this supply is payable by the customer under the reverse charge.</p>}
      {euGoods && <p className="el-paper-notice">Intra-Community supply of goods: zero rated. The customer’s VAT number is shown above.</p>}
      {on('notes') && doc.notes && <p className="el-paper-block">{doc.notes}</p>}
      {blocks('after_lines')}

      <footer className="el-paper-foot">
        {on('bank_details') && !credit && bank && <div>Pay by bank transfer: {bankLine(bank)}</div>}
        {blocks('footer')}
      </footer>
    </article>
  );
}

/** As the PDF: at least four decimals, up to six. */
export function shownRate(rate: string): string {
  return Number(rate).toFixed(6).replace(/0{1,2}$/, '').replace(/(\.\d{4})0$/, '$1');
}

function rateLabel(t: TaxCode | undefined): string {
  if (!t) return '—';
  if (t.code === 'EX') return 'Exempt';
  if (t.is_reverse_charge) return 'RC';
  return `${Number(t.rate).toString()}%`;
}
